import { createSignal, createResource, createMemo, createEffect, For, Show } from 'solid-js';
import RekapAbsensi from './RekapAbsensi.jsx';
import RekapLembur from './RekapLembur.jsx';
import RincianHarian from './RincianHarian.jsx';
import TimeInput24 from '../components/TimeInput24.jsx';
import { formatMoney, formatNumber, parseNumericInput } from '../lib/money.js';
import { runStatusLabel } from '../lib/labels.js';
import { me } from '../lib/session.js';

// Proses Gaji — run lifecycle: buat draft → isi input per periode → Hitung
// (snapshot variabel tetap + evaluasi + simpan hasil) → Finalisasi (beku).
// Input grid hanya menampilkan komponen input(...) dari versi skema yang
// DIPATOK oleh run; variabel tetap (Gaji Pokok dst.) diambil otomatis dari
// master saat Hitung.

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

async function send(method, url, body) {
  const res = await fetch(url, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

const STATUS_BADGE = { draft: 'warning', terbit: 'info', final: 'success', 'legacy-import': 'info' };

const WEEKDAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Suggest the next period start: the day AFTER the newest run's period end
// (period_end is NOT NULL, always serialized), so a run with a custom
// (shortened/extended) end chains into the next one with no gap — for
// default-length runs end+1 equals the old cadence stepping exactly. With no
// prior runs, align to the config: weekly (Mingguan) → the start weekday;
// monthly → the start day-of-month. sch may be undefined (→ monthly/21).
function nextPeriodStart(runs, sch) {
  const weekly = sch?.periodType === 'Mingguan';
  if (runs?.length) {
    const d = new Date(runs[0].periodEnd + 'T00:00:00'); // list is newest first
    d.setDate(d.getDate() + 1);
    return fmtDate(d);
  }
  const now = new Date();
  if (weekly) {
    const wd = sch?.startWeekday ?? 1;
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    while (d.getDay() !== wd) d.setDate(d.getDate() - 1); // most recent start weekday
    return fmtDate(d);
  }
  const day = sch?.startDay ?? 21;
  const d = new Date(now.getFullYear(), now.getMonth(), day);
  if (now.getDate() < day) d.setMonth(d.getMonth() - 1);
  return fmtDate(d);
}

// Default inclusive period end for a start date — mirrors the server's
// SchemePeriod.PeriodEnd (schemeperiod.go): weekly = start + 6 days, monthly =
// +1 month − 1 day. Prefills the "Periode akhir" input; the admin may edit it
// (shifted cutoff, short transition period) before creating the run.
function defaultPeriodEnd(startStr, sch) {
  if (!startStr) return '';
  const d = new Date(startStr + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  if (sch?.periodType === 'Mingguan') d.setDate(d.getDate() + 6);
  else { d.setMonth(d.getMonth() + 1); d.setDate(d.getDate() - 1); }
  return fmtDate(d);
}

export default function ProsesGaji() {
  const [schemes, { refetch: refetchSchemes }] = createResource(() => getJSON('/api/schemes'));
  const [scheme, setScheme] = createSignal('');
  const activeScheme = () => (schemes() ?? []).find((s) => s.code === scheme());
  createEffect(() => {
    const list = schemes();
    if (list?.length && !scheme()) setScheme(list[0].code);
  });

  const [runs, { refetch: refetchRuns }] = createResource(
    scheme,
    (code) => code ? getJSON(`/api/runs?scheme=${code}`) : null,
  );

  // Tipe Penggajian options for the active scheme (empty list → no tipe UI).
  const [schemeTipes] = createResource(
    scheme,
    (code) => code ? getJSON(`/api/scheme/${code}/tipes`) : null,
  );
  const hasTipes = () => (schemeTipes() ?? []).length > 0;
  // Run-create tipe choice: unchecked → unscoped run (all components).
  const [useTipe, setUseTipe] = createSignal(false);
  const [newTipe, setNewTipe] = createSignal('');
  createEffect(() => { const l = schemeTipes(); if (l?.length && !newTipe()) setNewTipe(l[0].code); });
  createEffect(() => { scheme(); setUseTipe(false); });

  const [selected, setSelected] = createSignal(0);
  const [detail, { refetch: refetchDetail }] = createResource(
    selected,
    (id) => id ? getJSON(`/api/run/${id}`) : null,
  );

  const [msg, setMsg] = createSignal(null);
  const [busy, setBusy] = createSignal(false);
  const [calcOut, setCalcOut] = createSignal(null);
  // True when an input was edited AFTER the last Hitung, so the shown results
  // are stale until the admin recalculates.
  const [dirtySinceCalc, setDirtySinceCalc] = createSignal(false);
  const [newStart, setNewStart] = createSignal('');
  const [newEnd, setNewEnd] = createSignal('');
  const [showText, setShowText] = createSignal(true);

  // ── Per-period grid filters (NIK/name search + departemen) ──────────────
  const [gridQ, setGridQ] = createSignal('');
  const [gridDept, setGridDept] = createSignal(''); // '' = all · '__none__' = no dept · else dept name
  // Reset the filters whenever the selected run changes so they don't carry over.
  createEffect(() => { selected(); setGridQ(''); setGridDept(''); });
  // Unique departments present in THIS run's roster (for the dropdown).
  const gridDeptNames = createMemo(() => {
    const s = new Set();
    for (const e of detail()?.employees ?? []) if (e.department) s.add(e.department);
    return [...s].sort((a, b) => a.localeCompare(b, 'id', { sensitivity: 'base' }));
  });
  // Roster rows after applying the search + departemen filters.
  const filteredEmployees = createMemo(() => {
    const term = gridQ().trim().toLowerCase();
    const d = gridDept();
    let rows = detail()?.employees ?? [];
    if (d) rows = rows.filter((e) => (d === '__none__' ? !e.department : e.department === d));
    if (term) rows = rows.filter((e) =>
      e.nik.toLowerCase().includes(term) || (e.name ?? '').toLowerCase().includes(term));
    return rows;
  });

  // Pay-period config effective for the run being CREATED: the selected tipe's
  // override (migration 046) when it sets one, else the scheme config. The
  // next-period suggestion also steps from the newest run OF THAT TIPE — mixed
  // periods (gaji from the 21st, komisi from the 1st) must not step from each
  // other's period starts.
  const tipeOverride = () => {
    if (!useTipe()) return null;
    const t = (schemeTipes() ?? []).find((x) => x.code === newTipe());
    return t?.periodType ? t : null;
  };
  const effPeriodCfg = () => {
    const base = activeScheme();
    const t = tipeOverride();
    if (!t) return base;
    return { ...base, periodType: t.periodType, startDay: t.periodStartDay || 21, startWeekday: t.periodStartWeekday ?? 1 };
  };
  const runsForSuggest = () => {
    const tipe = (useTipe() && hasTipes()) ? newTipe() : '';
    return (runs() ?? []).filter((r) => (r.tipe ?? '') === tipe);
  };
  createEffect(() => { setNewStart(nextPeriodStart(runsForSuggest(), effPeriodCfg())); });
  // The end suggestion re-derives whenever the start (or effective config)
  // changes — so a manual end edit survives until the admin picks a different
  // start, then snaps back to the computed default (predictable, never stale).
  // endTouched tracks whether the admin actually edited the end: an untouched
  // prefill posts periodEnd "" so the SERVER computes the default from its
  // authoritative config — the JS mirror is only a display suggestion.
  const [endTouched, setEndTouched] = createSignal(false);
  createEffect(() => { setNewEnd(defaultPeriodEnd(newStart(), effPeriodCfg())); setEndTouched(false); });
  // Non-blocking overlap warning: the new [start, end] range vs existing runs
  // of the SAME tipe (inclusive dates). Overlap pays the same days twice —
  // legal for corrections, but worth a heads-up before the run exists.
  const overlapRun = createMemo(() => {
    const s0 = newStart(), e0 = newEnd();
    if (!s0 || !e0) return null;
    return runsForSuggest().find((r) => s0 <= r.periodEnd && e0 >= r.periodStart) ?? null;
  });
  createEffect(() => { scheme(); setSelected(0); setMsg(null); setCalcOut(null); setDirtySinceCalc(false); });

  const isDraft = () => detail()?.run.status === 'draft';
  const isTerbit = () => detail()?.run.status === 'terbit';
  // Editable = draft OR terbit (a published run is still correctable until final).
  const isEditable = () => isDraft() || isTerbit();
  // Tipe Penggajian scoping: a run processed under a tipe shows only the
  // components tagged with it PLUS untagged ones. An unscoped run (run.tipe
  // empty) shows everything — today's behavior.
  const runTipe = () => detail()?.run?.tipe || '';
  const inScope = (c) => {
    const t = runTipe();
    if (!t) return true;
    const tipes = c.tipes || [];
    return tipes.length === 0 || tipes.includes(t);
  };
  // The tipe's needs-flags gate the attendance / overtime review panels. An
  // unscoped run keeps both (it may compute components that need either).
  // The run's tipe decides whether it uses the unified Rincian Harian (attendance
  // + lembur via the procs). No tipe → default true. Unchecked → the grid isn't
  // shown and its day-attrs proc is never called.
  const needsRincian = () => { const rt = detail()?.runTipe; return rt ? !!rt.needsRincian : true; };

  // Per-periode grid columns EXCLUDE globals (those render in their own panel)
  // and components out of the run's tipe scope.
  const numInputs = createMemo(() =>
    (detail()?.components ?? []).filter(c => c.mode === 'input' && c.dataType !== 'teks' && c.scope !== 'global' && inScope(c)));
  const textInputs = createMemo(() =>
    (detail()?.components ?? []).filter(c => c.mode === 'input' && c.dataType === 'teks' && c.scope !== 'global' && inScope(c)));
  // Global inputs: one value per run, set once for every employee.
  const globalInputs = createMemo(() =>
    (detail()?.components ?? []).filter(c => c.mode === 'input' && c.scope === 'global' && inScope(c)));
  const slipComps = createMemo(() =>
    (detail()?.components ?? []).filter(c => (c.kind === 'earning' || c.kind === 'deduction') && inScope(c)));
  const hasResults = createMemo(() =>
    detail() && Object.values(detail().results ?? {}).some(m => Object.keys(m).length > 0));

  // Per-period grid: the column whose cell is being edited (cursor inside it).
  // Its full name shows in a tooltip above the grid because headers are clipped.
  const [activeCol, setActiveCol] = createSignal(null);
  const [tipX, setTipX] = createSignal(0);
  function onCellFocus(c, el) {
    setActiveCol(c);
    const area = el.closest('.pg-grid-area');
    if (area) {
      const r = el.getBoundingClientRect(), a = area.getBoundingClientRect();
      setTipX(Math.round(r.left + r.width / 2 - a.left)); // centre the tip over the cell
    }
  }

  // ── Excel export (Daftar Gaji) ─────────────────────────────────────────
  // The dialog lets the admin toggle which slip components are exported. The
  // choice is REMEMBERED per scheme (stored as the EXCLUDED codes, so a newly
  // added component is included by default).
  const exportComps = createMemo(() =>
    (detail()?.components ?? []).filter(c => ['earning', 'deduction', 'info'].includes(c.kind) && inScope(c)));
  const [exportOpen, setExportOpen] = createSignal(false);
  const [excluded, setExcluded] = createSignal(new Set());
  const exportKey = () => `payroll_export_cols_${scheme()}`;
  const isIncluded = (code) => !excluded().has(code);
  const includedCount = () => exportComps().filter(c => isIncluded(c.code)).length;
  function persistExcluded(s) {
    try { localStorage.setItem(exportKey(), JSON.stringify([...s])); } catch (_) {}
  }
  function openExport() {
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(exportKey()) || '[]'); } catch (_) {}
    setExcluded(new Set(saved));
    setExportOpen(true);
  }
  function toggleComp(code, on) {
    const s = new Set(excluded());
    on ? s.delete(code) : s.add(code);
    setExcluded(s);
    persistExcluded(s);
  }
  function selectAll(on) {
    const s = on ? new Set() : new Set(exportComps().map(c => c.code));
    setExcluded(s);
    persistExcluded(s);
  }
  function doExport() {
    const codes = exportComps().map(c => c.code).filter(isIncluded);
    const url = `/api/run/${selected()}/salary-export?components=${encodeURIComponent(codes.join(','))}`;
    const a = document.createElement('a');
    a.href = url;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setExportOpen(false);
  }

  // ── Excel template + import for the per-periode grid ────────────────────
  // Template = the run's exact grid columns, pre-filled with current values
  // (doubles as an export). Import: empty cell = no change; invalid cells are
  // skipped + reported, and their NIK rows tint red in the grid.
  const [importMsg, setImportMsg] = createSignal(null); // {fail} | {written, errors}
  const [failedNiks, setFailedNiks] = createSignal(new Set());
  let importFileRef;
  createEffect(() => { selected(); setImportMsg(null); setFailedNiks(new Set()); }); // reset per run
  function downloadInputTemplate() {
    const a = document.createElement('a');
    a.href = `/api/run/${selected()}/input-template`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  async function importInputFile(e) {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = ''; // same file re-selectable
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/run/${selected()}/input-import`, { method: 'POST', body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setImportMsg({ fail: data.error || `HTTP ${res.status}` });
      setFailedNiks(new Set());
      return;
    }
    setImportMsg({ written: data.written, errors: data.errors ?? [] });
    setFailedNiks(new Set((data.errors ?? []).map((er) => er.nik).filter(Boolean)));
    refetchDetail();
  }
  // Bank-transfer list (fixed columns) — direct download, no column dialog.
  function doBankExport() {
    const a = document.createElement('a');
    a.href = `/api/run/${selected()}/bank-export`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function createRun() {
    setBusy(true);
    setMsg(null);
    const tipe = (useTipe() && hasTipes()) ? newTipe() : '';
    const r = await send('POST', '/api/runs',
      { scheme: scheme(), periodStart: newStart(), periodEnd: endTouched() ? newEnd() : '', tipe });
    setBusy(false);
    if (r.ok) {
      const tlabel = tipe ? ((schemeTipes() ?? []).find(t => t.code === tipe)?.label || tipe) : '';
      setMsg({ ok: true, msg: `Pemrosesan gaji dibuat (periode ${newStart()} — ${r.data.periodEnd}${tlabel ? `, tipe ${tlabel}` : ''}).` });
      refetchRuns();
      setSelected(r.data.id);
    } else setMsg({ ok: false, msg: r.data.error });
  }

  async function saveCell(nik, comp, value) {
    const r = await send('POST', `/api/run/${selected()}/input`,
      { nik, name: comp.code, value, isText: comp.dataType === 'teks' });
    if (!r.ok) setMsg({ ok: false, msg: r.data.error });
    else if (hasResults()) setDirtySinceCalc(true); // results now out of date
    return r.ok;
  }

  // One per-run global input (applies to every employee). Same staleness rule.
  async function saveGlobal(comp, value) {
    const r = await send('POST', `/api/run/${selected()}/global`, { name: comp.code, value });
    if (!r.ok) setMsg({ ok: false, msg: r.data.error });
    else if (hasResults()) setDirtySinceCalc(true);
    return r.ok;
  }

  async function calc() {
    setBusy(true);
    setMsg(null);
    const r = await send('POST', `/api/run/${selected()}/calc`);
    setBusy(false);
    if (r.ok) {
      setCalcOut(r.data);
      setDirtySinceCalc(false);
      setMsg({
        ok: r.data.errors.length === 0,
        msg: r.data.errors.length === 0
          ? `Berhasil dihitung untuk ${r.data.persons} karyawan.`
          : `Dihitung dengan ${r.data.errors.length} error — periksa daftar di bawah.`,
      });
      refetchDetail();
    } else setMsg({ ok: false, msg: r.data.error });
  }

  async function finalize() {
    if (!confirm('Finalisasi pemrosesan gaji ini? Setelah final, input dan hasil TIDAK BISA diubah lagi.')) return;
    setBusy(true);
    const r = await send('POST', `/api/run/${selected()}/finalize`);
    setBusy(false);
    setMsg(r.ok ? { ok: true, msg: 'Pemrosesan gaji difinalisasi — beku permanen.' } : { ok: false, msg: r.data.error });
    refetchRuns();
    refetchDetail();
  }

  // Publish a calculated draft: slips become visible to employees as SEMENTARA,
  // but the run stays editable so the admin can correct appeals before final.
  async function publish() {
    if (!confirm('Terbitkan pemrosesan gaji ini? Slip menjadi terlihat oleh karyawan sebagai SEMENTARA — Anda masih bisa mengoreksi lalu Finalisasi.')) return;
    setBusy(true);
    const r = await send('POST', `/api/run/${selected()}/publish`);
    setBusy(false);
    setMsg(r.ok ? { ok: true, msg: 'Diterbitkan (sementara) — slip terlihat oleh karyawan.' } : { ok: false, msg: r.data.error });
    refetchRuns(); refetchDetail();
  }
  async function unpublish() {
    if (!confirm('Tarik penerbitan? Slip kembali disembunyikan dari karyawan dan status kembali draft.')) return;
    setBusy(true);
    const r = await send('POST', `/api/run/${selected()}/unpublish`);
    setBusy(false);
    setMsg(r.ok ? { ok: true, msg: 'Penerbitan ditarik — kembali ke draft.' } : { ok: false, msg: r.data.error });
    refetchRuns(); refetchDetail();
  }

  // Add employees hired AFTER the run was created to its roster (the roster is
  // a snapshot taken at run creation and never grows on its own). Add-only —
  // the server reports inactive leftovers but never removes anyone.
  async function syncPersons() {
    setBusy(true);
    setMsg(null);
    const r = await send('POST', `/api/run/${selected()}/sync-persons`);
    setBusy(false);
    if (r.ok) {
      const n = r.data.added.length;
      let m = n === 0
        ? 'Tidak ada karyawan baru — daftar karyawan run ini sudah lengkap.'
        : `${n} karyawan ditambahkan: ${r.data.added.join(', ')}. Jalankan Hitung untuk menyertakan mereka.`;
      if (r.data.inactive.length) {
        m += ` Catatan: ${r.data.inactive.length} karyawan di daftar sudah nonaktif (tidak dihapus).`;
      }
      setMsg({ ok: true, msg: m });
      if (n > 0) {
        refetchDetail();
        refetchRuns();
        if (hasResults()) setDirtySinceCalc(true); // results now missing the newcomers
      }
    } else setMsg({ ok: false, msg: r.data.error });
  }

  // Take one employee out of a DRAFT run's roster — for the mis-registered
  // case (someone who never belonged to this scheme). Their inputs/results in
  // THIS run are deleted; the employee master is untouched.
  async function removePerson(emp) {
    if (!confirm(`Keluarkan ${emp.nik} — ${emp.name} dari pemrosesan ini?\nInput & hasil karyawan ini pada run ini akan dihapus (master karyawan tidak berubah).`)) return;
    setBusy(true);
    setMsg(null);
    const r = await send('DELETE', `/api/run/${selected()}/person/${encodeURIComponent(emp.nik)}`);
    setBusy(false);
    if (r.ok) {
      setMsg({ ok: true, msg: `${emp.nik} — ${emp.name} dikeluarkan dari pemrosesan ini.` });
      setCalcOut(null); // stale eval errors may reference the removed employee
      refetchDetail();
      refetchRuns();
    } else setMsg({ ok: false, msg: r.data.error });
  }

  async function removeRun() {
    if (!confirm('Hapus pemrosesan gaji ini beserta seluruh input dan hasilnya?')) return;
    setBusy(true);
    const r = await send('DELETE', `/api/run/${selected()}`);
    setBusy(false);
    if (r.ok) {
      setMsg({ ok: true, msg: 'Pemrosesan gaji dihapus.' });
      setSelected(0);
      setCalcOut(null);
      setDirtySinceCalc(false);
      refetchRuns();
    } else setMsg({ ok: false, msg: r.data.error });
  }

  // Bruto / potongan / bersih per employee, from stored results.
  function slipTotals(nik) {
    const res = detail()?.results?.[nik] ?? {};
    let bruto = 0, potongan = 0;
    for (const c of slipComps()) {
      if (c.skipTotal) continue; // printed on the slip, never counted
      const v = parseFloat(res[c.code] ?? '0') || 0;
      if (c.kind === 'earning') bruto += v;
      else potongan += v;
    }
    return { bruto, potongan, bersih: bruto - potongan };
  }
  const fmt = formatMoney; // Indonesian locale + configurable decimals

  return (
    <>
      <div class="page-head">
        <h2>Pemrosesan Gaji</h2>
        <div class="spacer" />
        <Show when={schemes()}>
          <div class="seg">
            <For each={schemes()}>{(s) =>
              <button class="seg-item" classList={{ active: scheme() === s.code }}
                onClick={() => setScheme(s.code)}>{s.code}</button>
            }</For>
          </div>
        </Show>
      </div>

      {/* ── Run list + create ─────────────────────────────────────────── */}
      <div class="card card-pad pg-runs">
        <div class="pg-create">
          <label class="kg-field">
            <span>Periode mulai</span>
            <input type="date" value={newStart()} onInput={(e) => setNewStart(e.currentTarget.value)} />
          </label>
          <label class="kg-field">
            <span>Periode akhir</span>
            <input type="date" value={newEnd()}
              onInput={(e) => { setNewEnd(e.currentTarget.value); setEndTouched(true); }}
              title="Terisi otomatis dari konfigurasi periode — ubah untuk cutoff yang digeser / periode transisi pendek" />
          </label>
          <Show when={hasTipes()}>
            <label class="kg-field">
              <span>Tipe penggajian</span>
              <div class="pg-tipe-pick">
                <label class="ky-check" title="Jika dicentang, hanya komponen bertag tipe ini (+ tanpa tag) yang dihitung">
                  <input type="checkbox" checked={useTipe()} onInput={(e) => setUseTipe(e.currentTarget.checked)} />
                  pakai tipe
                </label>
                <Show when={useTipe()} fallback={<span class="kg-meta-text">semua komponen</span>}>
                  <select class="kg-select" value={newTipe()} onInput={(e) => setNewTipe(e.currentTarget.value)}>
                    <For each={schemeTipes()}>{(t) => <option value={t.code}>{t.label}</option>}</For>
                  </select>
                  <Show when={tipeOverride()}>
                    <span class="kg-meta-text" title="Tipe ini memakai periode sendiri (bukan periode skema)">
                      periode: {tipeOverride().periodType === 'Mingguan'
                        ? `Mingguan · ${WEEKDAYS[tipeOverride().periodStartWeekday ?? 1]}`
                        : `Bulanan · tgl ${tipeOverride().periodStartDay || 21}`}
                    </span>
                  </Show>
                </Show>
              </div>
            </label>
          </Show>
          <button class="btn primary" disabled={busy()} onClick={createRun}>Buat Pemrosesan Gaji</button>
          <div class="spacer" />
          <span class="kg-meta-text">{(runs() ?? []).length} pemrosesan</span>
        </div>
        <Show when={overlapRun()}>
          <div class="pg-stale">
            ⚠ Periode {newStart()} — {newEnd()} tumpang tindih dengan pemrosesan
            {' '}{overlapRun().periodStart} — {overlapRun().periodEnd}
            {overlapRun().tipeLabel ? ` (tipe ${overlapRun().tipeLabel})` : ''} — hari yang sama bisa terbayar dua kali.
            Boleh dilanjutkan jika memang disengaja (mis. koreksi).
          </div>
        </Show>
        <Show when={activeScheme()}>
          <PeriodConfig scheme={activeScheme()} onSaved={refetchSchemes} setMsg={setMsg} />
        </Show>
        <div class="pg-run-list">
          <For each={runs() ?? []}>{(r) =>
            <button class="pg-run" classList={{ active: selected() === r.id }}
              onClick={() => { setSelected(r.id); setMsg(null); setCalcOut(null); setDirtySinceCalc(false); }}>
              <span class="pg-run-period">{r.periodStart}</span>
              <span class={`badge ${STATUS_BADGE[r.status] ?? 'info'}`}>{runStatusLabel(r.status)}</span>
              <Show when={r.tipe}><span class="badge info">{r.tipe}</span></Show>
              <span class="pg-run-meta">{r.persons} kary.{r.legacyNo ? ` · ${r.legacyNo}` : ` · v${r.schemeVersion}`}</span>
            </button>
          }</For>
        </div>
      </div>

      <Show when={msg()}>
        <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin-top:10px">{msg().msg}</div>
      </Show>

      {/* ── Run detail ───────────────────────────────────────────────── */}
      <Show when={detail()}>
        <div class="card card-pad pg-detail">
          <div class="pg-detail-head">
            <div>
              <h3 class="ky-name">Periode {detail().run.periodStart} — {detail().run.periodEnd}</h3>
              <div class="kg-meta-text">
                {detail().run.scheme} v{detail().run.schemeVersion || '—'} ·
                {' '}{detail().employees.length} karyawan ·
                {' '}<span class={`badge ${STATUS_BADGE[detail().run.status] ?? 'info'}`}>{runStatusLabel(detail().run.status)}</span>
                <Show when={detail().run.tipe}>
                  {' '}· <span class="badge info">Tipe: {detail().runTipe?.label || detail().run.tipe}</span>
                </Show>
                <Show when={detail().run.attendanceProc || detail().run.overtimeProc}>
                  {' '}· <span class="badge info" title="Derivasi via stored procedure, dipatok pada run ini">
                    proc: {detail().run.attendanceProc || '—'}{detail().run.overtimeProc ? ` / ${detail().run.overtimeProc}` : ''}
                  </span>
                </Show>
              </div>
            </div>
            <div class="spacer" />
            <Show when={hasResults()}>
              <button class="btn" onClick={openExport} title="Export Daftar Gaji ke Excel">⤓ Export Excel</button>
              <button class="btn" onClick={doBankExport} title="Export daftar transfer bank ke Excel">⤓ Transfer Bank</button>
            </Show>
            <Show when={isEditable()}>
              <Show when={isDraft()}>
                <button class="btn" disabled={busy()} onClick={removeRun}>Hapus</button>
              </Show>
              <button class="btn" disabled={busy()} onClick={syncPersons}
                title="Tambahkan karyawan aktif yang dimasukkan SETELAH run ini dibuat ke daftar karyawan run (tidak menghapus siapa pun)">
                Perbarui Karyawan
              </button>
              <button class="btn" disabled={busy()} onClick={calc}>{busy() ? 'Menghitung…' : 'Hitung'}</button>
              <Show when={isDraft()}>
                <button class="btn" disabled={busy() || !hasResults()} onClick={publish}
                  title={hasResults() ? 'Terbitkan slip sementara (terlihat karyawan)' : 'Jalankan Hitung dulu'}>
                  Terbitkan
                </button>
              </Show>
              <Show when={isTerbit()}>
                <button class="btn" disabled={busy()} onClick={unpublish} title="Sembunyikan slip & kembali ke draft">Tarik</button>
              </Show>
              <button class="btn primary" disabled={busy() || !hasResults()} onClick={finalize}
                title={hasResults() ? 'Bekukan pemrosesan gaji ini (final)' : 'Jalankan Hitung dulu'}>
                Finalisasi
              </button>
            </Show>
          </div>

          <Show when={isTerbit()}>
            <div class="pg-stale" style="background:var(--info-soft,rgba(59,130,246,0.12))">
              ⓘ Pemrosesan ini <b>terbit (sementara)</b> — slip sudah terlihat oleh karyawan namun belum final.
              Koreksi lalu <b>Hitung</b> ulang bila ada keberatan, atau <b>Finalisasi</b> untuk membekukan.
            </div>
          </Show>

          {/* ── Attendance review + prefill — NON-proc schemes only. Proc schemes
                 review + edit attendance entirely in the unified Rincian Harian
                 below (15-derivation-contract). Draft only; skipped when the run's
                 tipe doesn't need absensi data. ───────────────────────────── */}
          <Show when={isEditable() && needsRincian() && !detail().run.attendanceProc ? detail().run.id : null} keyed>
            {(id) => <RekapAbsensi runId={id} onApplied={refetchDetail} />}
          </Show>

          {/* ── Overtime (Lembur SPL) review + prefill — NON-proc schemes only.
                 Proc overtime folds into the unified Rincian Harian in a later
                 phase; until then it's hidden so the (still-legacy) overtime proc
                 isn't called. Draft only; skipped when the tipe needs no lembur. */}
          <Show when={isEditable() && needsRincian() && !detail().run.overtimeProc ? detail().run.id : null} keyed>
            {(id) => <RekapLembur runId={id} onApplied={refetchDetail} />}
          </Show>

          {/* ── Unified Rincian Harian (attendance + lembur via the procs). Shown
                 only when the run's tipe needs it — otherwise the grid is hidden
                 AND the day-attrs stored procedure is never called. ─────────── */}
          <Show when={needsRincian() ? detail().run.id : null} keyed>
            {(id) => <RincianHarian runId={id} employees={detail().employees} onApplied={refetchDetail} />}
          </Show>

          {/* ── Global inputs (one value per run, all employees) ────── */}
          <Show when={globalInputs().length > 0}>
            <div class="ky-vars-head">
              <h4 class="kg-hist-title">Input global</h4>
            </div>
            <p class="ky-vars-note">Berlaku untuk semua karyawan pada periode ini — diisi sekali.</p>
            <div class="pg-globals">
              <For each={globalInputs()}>{(c) =>
                <label class="kg-field pg-global">
                  <span title={c.code}>{c.label || c.code}</span>
                  <GlobalField comp={c} value={detail().globals?.[c.code] ?? ''}
                    readonly={!isEditable()} onSave={(v) => saveGlobal(c, v)} />
                </label>
              }</For>
            </div>
          </Show>

          {/* ── Per-period input grid ──────────────────────────────── */}
          <div class="ky-vars-head pg-grid-head">
            <h4 class="kg-hist-title">Input per periode</h4>
            <input class="pg-grid-search" type="search" placeholder="Cari NIK / nama…"
                   value={gridQ()} onInput={(e) => setGridQ(e.currentTarget.value)} />
            <Show when={gridDeptNames().length > 0}>
              <select class="kg-select" value={gridDept()} onInput={(e) => setGridDept(e.currentTarget.value)}>
                <option value="">Semua departemen</option>
                <option value="__none__">Tanpa departemen</option>
                <For each={gridDeptNames()}>{(d) => <option value={d}>{d}</option>}</For>
              </select>
            </Show>
            <Show when={gridQ() || gridDept()}>
              <span class="kg-meta-text">{filteredEmployees().length} / {detail().employees.length}</span>
            </Show>
            <div class="spacer" />
            <button class="btn ghost" onClick={downloadInputTemplate}
                    title="Unduh template Excel — kolom sesuai grid ini, terisi nilai saat ini">⤓ Template</button>
            <Show when={isEditable()}>
              <button class="btn ghost" onClick={() => importFileRef.click()}
                      title="Impor template yang sudah diisi">⤴ Impor</button>
              <input type="file" accept=".xlsx" style="display:none"
                     ref={importFileRef} onInput={importInputFile} />
            </Show>
            <Show when={textInputs().length > 0}>
              <label class="ky-check">
                <input type="checkbox" checked={showText()} onInput={(e) => setShowText(e.currentTarget.checked)} />
                tampilkan kolom teks
              </label>
            </Show>
          </div>
          <Show when={importMsg()}>
            <div class={`kg-verdict ${importMsg().fail || importMsg().errors?.length ? 'fail' : 'ok'}`} style="margin:6px 0">
              <Show when={!importMsg().fail} fallback={importMsg().fail}>
                {importMsg().written} nilai diimpor.
                <Show when={importMsg().errors?.length > 0}>
                  {' '}{importMsg().errors.length} ditolak (baris merah di grid):
                  <div class="pg-import-errs">
                    <For each={importMsg().errors}>{(er) =>
                      <div>baris {er.row}{er.nik ? ` · ${er.nik}` : ''}{er.code ? ` · ${er.code}` : ''} — {er.error}</div>
                    }</For>
                  </div>
                </Show>
              </Show>
            </div>
          </Show>
          <div class="pg-grid-area" onFocusOut={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setActiveCol(null); }}>
            <Show when={activeCol()}>
              <div class="pg-col-tip" style={`left:${tipX()}px`}>
                <b>{activeCol().code}</b><Show when={activeCol().label && activeCol().label !== activeCol().code}> · {activeCol().label}</Show>
              </div>
            </Show>
            <div class="pg-grid-wrap">
            <table class="data pg-grid">
              <thead>
                <tr>
                  <th class="pg-sticky">Karyawan</th>
                  <For each={numInputs()}>{(c) => <th class="num" classList={{ 'pg-col-active': activeCol()?.code === c.code }} title={c.code}>{c.code}</th>}</For>
                  <Show when={showText()}>
                    <For each={textInputs()}>{(c) => <th classList={{ 'pg-col-active': activeCol()?.code === c.code }} title={c.code}>{c.code}</th>}</For>
                  </Show>
                </tr>
              </thead>
              <tbody>
                <Show when={filteredEmployees().length === 0}>
                  <tr>
                    <td class="kg-sub" colspan={1 + numInputs().length + (showText() ? textInputs().length : 0)}>
                      Tidak ada karyawan yang cocok dengan filter.
                    </td>
                  </tr>
                </Show>
                <For each={filteredEmployees()}>{(emp) =>
                  <tr classList={{ 'pg-row-err': failedNiks().has(emp.nik) }}>
                    <td class="pg-sticky">
                      <div class="kg-code">
                        {emp.nik}
                        <Show when={isDraft() && emp.stray}>
                          <button class="kg-del pg-emp-del" disabled={busy()} onClick={() => removePerson(emp)}
                            title={`Keluarkan ${emp.nik} dari pemrosesan ini — ${emp.strayReason}`}>✕</button>
                        </Show>
                      </div>
                      <div class="kg-sub">{emp.name}</div>
                      <Show when={emp.stray}>
                        <span class="badge warning pg-stray" title="Tidak akan ikut jika run dibuat sekarang — data master berubah setelah run dibuat">
                          ⚠ {emp.strayReason}
                        </span>
                      </Show>
                    </td>
                    <For each={numInputs()}>{(c) =>
                      <td class="num">
                        <GridCell
                          value={detail().inputs[emp.nik]?.[c.code] ?? ''}
                          numeric readonly={!isEditable()}
                          onSave={(v) => saveCell(emp.nik, c, v)}
                          onFocus={(el) => onCellFocus(c, el)}
                        />
                      </td>
                    }</For>
                    <Show when={showText()}>
                      <For each={textInputs()}>{(c) =>
                        <td>
                          <GridCell
                            value={detail().inputs[emp.nik]?.[c.code] ?? ''}
                            choices={c.choices}
                            readonly={!isEditable()}
                            onSave={(v) => saveCell(emp.nik, c, v)}
                            onFocus={(el) => onCellFocus(c, el)}
                          />
                        </td>
                      }</For>
                    </Show>
                  </tr>
                }</For>
              </tbody>
            </table>
            </div>
          </div>

          {/* ── Stale-results warning ──────────────────────────────── */}
          <Show when={dirtySinceCalc() && hasResults() && isEditable()}>
            <div class="pg-stale">⚠ Input berubah sejak Hitung terakhir — klik <b>Hitung</b> lagi agar hasil di bawah sesuai.</div>
          </Show>

          {/* ── Calc errors ────────────────────────────────────────── */}
          <Show when={calcOut()?.errors?.length > 0}>
            <h4 class="kg-hist-title">Error evaluasi</h4>
            <For each={calcOut().errors}>{(e) =>
              <div class="ferr">⚠ {e.nik} · {e.code}: {e.msg}</div>
            }</For>
          </Show>

          {/* ── Results ────────────────────────────────────────────── */}
          <Show when={hasResults()}>
            <h4 class="kg-hist-title">Hasil per karyawan</h4>
            <table class="data">
              <thead>
                <tr><th>Karyawan</th><th class="num">Pendapatan</th><th class="num">Potongan</th><th class="num">Bersih</th></tr>
              </thead>
              <tbody>
                <For each={detail().employees}>{(emp) => {
                  const t = slipTotals(emp.nik);
                  return (
                    <>
                      <tr class="pg-emp-row" onClick={(e) => {
                        const next = e.currentTarget.nextElementSibling;
                        next.classList.toggle('open');
                      }}>
                        <td><b>{emp.nik}</b> {emp.name}</td>
                        <td class="num money earning">{fmt(t.bruto)}</td>
                        <td class="num money deduction">{fmt(t.potongan)}</td>
                        <td class="num money"><b>{fmt(t.bersih)}</b></td>
                      </tr>
                      <tr class="pg-slip-row">
                        <td colspan="4">
                          <div class="pg-slip">
                            <For each={slipComps().filter(c => parseFloat(detail().results[emp.nik]?.[c.code] ?? '0') !== 0)}>{(c) =>
                              <div class="pg-slip-line">
                                <span>{c.label}</span>
                                <span class={`money ${c.kind}`}>{fmt(detail().results[emp.nik]?.[c.code])}</span>
                              </div>
                            }</For>
                          </div>
                        </td>
                      </tr>
                    </>
                  );
                }}</For>
              </tbody>
            </table>
          </Show>
        </div>
      </Show>

      {/* ── Export dialog: pick which komponen gaji go into the Excel ──── */}
      <Show when={exportOpen()}>
        <div class="modal-backdrop" onClick={() => setExportOpen(false)}>
          <div class="modal-card card card-pad" onClick={(e) => e.stopPropagation()}>
            <div class="ky-vars-head">
              <h4 class="kg-hist-title">Export Daftar Gaji — Excel</h4>
              <div class="spacer" />
              <button class="btn ghost" onClick={() => setExportOpen(false)}>✕</button>
            </div>
            <p class="ky-vars-note">
              Pilih komponen gaji yang ikut diekspor. Pilihan ini <b>diingat</b> untuk skema {scheme()}.
              NIK, Nama, Departemen, total potongan/pendapatan, dan gaji bersih selalu disertakan.
            </p>
            <div class="export-actions">
              <button class="btn ghost" onClick={() => selectAll(true)}>Pilih semua</button>
              <button class="btn ghost" onClick={() => selectAll(false)}>Kosongkan</button>
              <div class="spacer" />
              <span class="kg-meta-text">{includedCount()} / {exportComps().length} komponen</span>
            </div>
            <div class="export-comp-list">
              <For each={exportComps()}>{(c) =>
                <label class="ky-check export-comp">
                  <input type="checkbox" checked={isIncluded(c.code)} onInput={(e) => toggleComp(c.code, e.currentTarget.checked)} />
                  <span class={`badge ${c.kind === 'earning' ? 'success' : c.kind === 'deduction' ? 'danger' : 'info'}`}>
                    {c.kind === 'earning' ? '+' : c.kind === 'deduction' ? '−' : 'i'}
                  </span>
                  {c.label || c.code}
                </label>
              }</For>
            </div>
            <div class="export-foot">
              <div class="spacer" />
              <button class="btn ghost" onClick={() => setExportOpen(false)}>Batal</button>
              <button class="btn primary" onClick={doExport}>⤓ Export Excel</button>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
}

// GridCell: lightweight editable cell; saves on blur/Enter when changed.
// An enum teks input (props.choices) renders as a dropdown instead — the only
// way to set the value, so a typo can't silently zero a slip.
function GridCell(props) {
  const [val, setVal] = createSignal(props.value);
  const [editing, setEditing] = createSignal(false);
  createEffect(() => setVal(props.value)); // refresh after refetch
  async function commit() {
    // Normalise grouped/pasted numbers (e.g. Excel "216,039,234") to the engine's
    // bare-digit grammar before saving; text cells pass through untouched.
    const next = props.numeric ? parseNumericInput(val()) : val();
    if (next === props.value) return;
    const ok = await props.onSave(next);
    if (ok) setVal(next); else setVal(props.value); // keep canonical, or revert on rejection
  }
  // While focused show the raw value (editing); when blurred a numeric cell shows
  // the id-ID thousand separator. DISPLAY-ONLY — val()/onSave stay the raw number.
  const shown = () => (props.numeric && !editing() ? formatNumber(val()) : val());
  return (
    <Show when={props.choices?.length} fallback={
      <input
        class="pg-cell"
        classList={{ 'pg-cell-num': props.numeric, filled: val() !== '' }}
        type="text"
        inputmode={props.numeric ? 'decimal' : 'text'}
        value={shown()}
        readonly={props.readonly}
        onFocus={(e) => { setEditing(true); props.onFocus?.(e.currentTarget); }}
        onInput={(e) => setVal(e.currentTarget.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        onBlur={() => { setEditing(false); commit(); }}
      />
    }>
      <select class="pg-cell-select" classList={{ filled: val() !== '' }}
        value={val()} disabled={props.readonly}
        onFocus={(e) => props.onFocus?.(e.currentTarget)}
        onChange={async (e) => {
          const v = e.currentTarget.value;
          setVal(v);
          const ok = await props.onSave(v);
          if (!ok) setVal(props.value);
        }}>
        <option value="">—</option>
        <For each={props.choices}>{(opt) => <option value={opt}>{opt}</option>}</For>
      </select>
    </Show>
  );
}

// GlobalField: the control for one per-run global input in the global panel.
// Enum teks → dropdown; angka → right-aligned number field; free teks → text.
function GlobalField(props) {
  const c = props.comp;
  const [val, setVal] = createSignal(props.value);
  const [editing, setEditing] = createSignal(false);
  createEffect(() => setVal(props.value));
  const numeric = c.dataType !== 'teks';
  async function commit(v) {
    const next = numeric ? parseNumericInput(v) : v;
    if (next === props.value) return;
    const ok = await props.onSave(next);
    if (ok) setVal(next); else setVal(props.value);
  }
  const shown = () => (numeric && !editing() ? formatNumber(val()) : val());
  return (
    <Show when={c.dataType === 'teks' && c.choices?.length} fallback={
      <input
        class="pg-global-input"
        classList={{ 'pg-cell-num': numeric }}
        type="text"
        inputmode={c.dataType === 'teks' ? 'text' : 'decimal'}
        value={shown()}
        readonly={props.readonly}
        onFocus={() => setEditing(true)}
        onInput={(e) => setVal(e.currentTarget.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        onBlur={() => { setEditing(false); commit(val()); }}
      />
    }>
      <select class="kg-select" value={val()} disabled={props.readonly}
        onChange={(e) => { setVal(e.currentTarget.value); commit(e.currentTarget.value); }}>
        <For each={c.choices}>{(opt) => <option value={opt}>{opt}</option>}</For>
      </select>
    </Show>
  );
}

// PeriodConfig — exposes & edits the SCHEME's pay-period setting (monthly 21st→
// 20th, or weekly for TKBM). The pay period belongs to the scheme, not a global
// setting; changing it here affects how new pemrosesan gaji periods are framed.
function PeriodConfig(props) {
  const [open, setOpen] = createSignal(false);
  const [ptype, setPtype] = createSignal(props.scheme.periodType);
  const [day, setDay] = createSignal(props.scheme.startDay);
  const [wd, setWd] = createSignal(props.scheme.startWeekday);
  const [minLembur, setMinLembur] = createSignal(props.scheme.lemburMinStart || '');
  const [attProc, setAttProc] = createSignal(props.scheme.attendanceProc || '');
  const [otProc, setOtProc] = createSignal(props.scheme.overtimeProc || '');
  createEffect(() => {
    setPtype(props.scheme.periodType); setDay(props.scheme.startDay); setWd(props.scheme.startWeekday);
    setMinLembur(props.scheme.lemburMinStart || '');
    setAttProc(props.scheme.attendanceProc || ''); setOtProc(props.scheme.overtimeProc || '');
  });
  async function save() {
    const r = await send('POST', `/api/scheme/${props.scheme.code}/period`,
      { periodType: ptype(), startDay: Number(day()) || 21, startWeekday: Number(wd()), lemburMinStart: minLembur() });
    if (!r.ok) { props.setMsg({ ok: false, msg: r.data.error }); return; }
    // Derivation proc selector — saved alongside; empty = built-in Go derivation.
    const r2 = await send('POST', `/api/scheme/${props.scheme.code}/derivation`,
      { attendanceProc: attProc().trim(), overtimeProc: otProc().trim() });
    if (r2.ok) { props.setMsg({ ok: true, msg: 'Pengaturan skema tersimpan.' }); setOpen(false); props.onSaved(); }
    else props.setMsg({ ok: false, msg: r2.data.error });
  }
  return (
    <div class="pg-period">
      <span class="kg-meta-text">
        Periode penggajian: <b>{props.scheme.periodType}</b>
        {props.scheme.periodType === 'Mingguan'
          ? ` · mulai hari ${WEEKDAYS[props.scheme.startWeekday]}`
          : ` · mulai tanggal ${props.scheme.startDay}`}
        {props.scheme.lemburMinStart ? ` · lembur mulai ≥ ${props.scheme.lemburMinStart}` : ''}
        {props.scheme.attendanceProc ? ` · absensi: proc ${props.scheme.attendanceProc}` : ''}
        {props.scheme.overtimeProc ? ` · lembur: proc ${props.scheme.overtimeProc}` : ''}
      </span>
      <button class="btn ghost" onClick={() => setOpen(!open())}>{open() ? 'tutup' : 'atur'}</button>
      <Show when={open()}>
        <div class="pg-period-edit">
          <select class="kg-select" value={ptype()} onInput={(e) => setPtype(e.currentTarget.value)}>
            <option value="Bulanan">Bulanan (per bulan)</option>
            <option value="Mingguan">Mingguan (per minggu)</option>
          </select>
          <Show when={ptype() === 'Mingguan'} fallback={
            <label class="kg-field"><span>Tanggal mulai</span>
              <input type="number" min="1" max="28" value={day()} onInput={(e) => setDay(e.currentTarget.value)} /></label>
          }>
            <label class="kg-field"><span>Mulai hari</span>
              <select class="kg-select" value={wd()} onInput={(e) => setWd(e.currentTarget.value)}>
                <For each={WEEKDAYS}>{(d, i) => <option value={i()}>{d}</option>}</For>
              </select></label>
          </Show>
          <label class="kg-field"><span>Jam lembur minimal</span>
            <TimeInput24 value={minLembur()} onChange={setMinLembur} placeholder="HH:MM (kosong = nonaktif)" />
          </label>
          <label class="kg-field"><span>Proc derivasi absensi</span>
            <input type="text" value={attProc()} onInput={(e) => setAttProc(e.currentTarget.value)}
              placeholder="kosong = derivasi bawaan (Go)" />
          </label>
          <label class="kg-field"><span>Proc derivasi lembur</span>
            <input type="text" value={otProc()} onInput={(e) => setOtProc(e.currentTarget.value)}
              placeholder="kosong = derivasi bawaan (Go)" />
          </label>
          <p class="kg-meta-text" style="flex-basis:100%;margin:2px 0 0">
            Stored procedure milik pemilik (mis. <code>sp_derive_tkbm_v1</code>) yang menghitung input
            absensi/lembur. Berlaku untuk pemrosesan <b>baru</b>; run yang sudah ada tetap memakai proc
            yang dipatoknya. Ganti aturan = proc baru, lalu arahkan ke sini.
          </p>
          <button class="btn primary" onClick={save}>Simpan</button>
        </div>
      </Show>
    </div>
  );
}
